import { Link, useSearchParams, useLocation, useParams } from "react-router";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useMemo } from "react";

/**
 * Formats children names with bullet separator.
 * Example: "Jules ∙ Pierre ∙ Artémise" for ["Jules", "Pierre", "Artémise"]
 */
function formatChildrenNames(names: string[]): string {
  if (names.length === 0) return "";
  return names.join(" ∙ ");
}

/**
 * Persistent Breadcrumb component for registration pages.
 * This component detects the current route and renders appropriate breadcrumbs
 * with smooth animations between states.
 *
 * Routes handled:
 * - /dash/registrations → "Inscriptions" (large)
 * - /dash/registrations/:id → "Inscriptions > [Children Names]"
 * - /dash/registrations/:id/edit → "Inscriptions > [Children Names] > Édition"
 * - /dash/registrations/new → "Inscriptions > Nouvelle inscription"
 */
export function
RegistrationBreadcrumb() {
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "all";

  // Determine the current page type based on route
  const pageType = useMemo(() => {
    const path = location.pathname;

    if (path === "/dash/registrations") {
      return "list";
    }
    if (path === "/dash/registrations/new") {
      return "new";
    }
    if (path.match(/^\/dash\/registrations\/[^/]+\/edit$/)) {
      return "edit";
    }
    if (path.match(/^\/dash\/registrations\/[^/]+$/)) {
      return "details";
    }
    return "list";
  }, [location.pathname]);

  // Fetch registration data if we're on a details or edit page
  const registrationId = params.id;
  const { data: registrationData } = api.useQuery(
    "get",
    "/registrations/{id}",
    {
      params: { path: { id: registrationId || "" } },
    },
    {
      enabled: !!registrationId && (pageType === "details" || pageType === "edit"),
      staleTime: 60000,
    }
  );

  // Extract children names from registration data
  const children = registrationData?.children;
  const childrenNames = useMemo(() => {
    if (!children) return [];
    return children.map((child: { firstName: string }) => child.firstName);
  }, [children]);

  const formattedNames = formatChildrenNames(childrenNames);
  const isMainPage = pageType === "list";

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {/* First item: Inscriptions - animated size change */}
        <BreadcrumbItem className={cn("transition-all duration-300 ease-in-out", isMainPage ? "text-2xl font-bold text-gray-800" : "text-base")}>
          {isMainPage ? (
            <BreadcrumbPage className="font-bold">Inscriptions</BreadcrumbPage>
          ) : (
            <BreadcrumbLink asChild>
              <Link to={`/dash/registrations?tab=${activeTab}`}>Inscriptions</Link>
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>

        {/* Second item: Children names or "Nouvelle inscription" */}
        <div
          className={cn(
            "flex items-center gap-1.5 overflow-hidden transition-all duration-300 ease-in-out sm:gap-2.5",
            isMainPage ? "max-w-0 opacity-0" : "max-w-[600px] opacity-100"
          )}
        >
          <BreadcrumbSeparator className="translate-y-px transition-opacity duration-200" />
          <BreadcrumbItem className="max-w-[150px] text-base sm:max-w-[250px] md:max-w-[350px] lg:max-w-[450px]">
            {pageType === "new" ? (
              <BreadcrumbPage>Nouvelle inscription</BreadcrumbPage>
            ) : pageType === "edit" && registrationId ? (
              <BreadcrumbLink asChild className="block truncate">
                <Link to={`/dash/registrations/${registrationId}?tab=${activeTab}`}>{formattedNames || "..."}</Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage className="block truncate">{formattedNames || "..."}</BreadcrumbPage>
            )}
          </BreadcrumbItem>
        </div>

        {/* Third item: "Édition" for edit page */}
        <div
          className={cn(
            "flex items-center gap-1.5 overflow-hidden transition-all duration-300 ease-in-out sm:gap-2.5",
            pageType === "edit" ? "max-w-[200px] opacity-100" : "max-w-0 opacity-0"
          )}
        >
          <BreadcrumbSeparator className="translate-y-px transition-opacity duration-200" />
          <BreadcrumbItem className="text-base">
            <BreadcrumbPage>Édition</BreadcrumbPage>
          </BreadcrumbItem>
        </div>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
